// src/components/backend/companies/utils/companyUtils.js

export const CUSTNO_RE = /^[A-Za-z0-9_-]+$/;

export function getCellValue(row, key) {
    switch (key) {
        case "is_active":
            return row?.is_active ? 1 : 0;
        case "customer_number":
            return row?.customer_number ?? "";
        case "name":
            return row?.name ?? "";
        case "street":
            return row?.street ?? "";
        case "zip":
            return row?.zip ?? "";
        case "city":
            return row?.city ?? "";
        case "country":
            return row?.country ?? "";
        case "lat":
            return row?.latitude ?? null;
        case "lon":
            return row?.longitude ?? null;
        case "page_views":
            return row?.page_views ?? 0;
        case "last_change":
            return row?.last_change?.changed_at ?? "";
        default:
            return "";
    }
}

export function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);

    const na = typeof va === "number" ? va : Number(va);
    const nb = typeof vb === "number" ? vb : Number(vb);

    if (Number.isFinite(na) && Number.isFinite(nb)) return nb - na;

    return (vb ?? "").toString().localeCompare((va ?? "").toString(), "de", {
        sensitivity: "base",
    });
}

export function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort(array, comparator) {
    return [...(array || [])]
        .map((el, idx) => [el, idx])
        .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
        .map((el) => el[0]);
}

export function formatCoord(value) {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(6) : String(value);
}

export function normalizeIdArray(arr) {
    if (!Array.isArray(arr)) return [];
    return arr.map((x) => Number(x)).filter((x) => Number.isFinite(x));
}

export function includesInsensitive(haystack, needle) {
    if (!needle) return true;
    return String(haystack ?? "")
        .toLowerCase()
        .includes(String(needle).toLowerCase().trim());
}

export function companyMatchesSearch(company, needle) {
    if (!needle?.trim()) return true;
    const n = needle.trim().toLowerCase();

    const fields = [
        company?.name,
        company?.customer_number,
        company?.street,
        company?.zip,
        company?.city,
        company?.country,
        company?.latitude,
        company?.longitude,
    ];

    return fields.some((f) => includesInsensitive(f, n));
}

export function resolveUrl(url, apiBase = "") {
    // Comments in English: Ensure images are loaded from backend when url is relative (/uploads/...).
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;

    const base = (apiBase || "").replace(/\/+$/, "");
    if (!base) return url;

    if (!url.startsWith("/")) return `${base}/${url}`;
    return `${base}${url}`;
}

export function sortImagesLocal(images) {
    // Comments in English: Sort by sort_order first, then id for stability.
    return [...(images || [])].sort((a, b) => {
        const sa = Number.isFinite(Number(a?.sort_order)) ? Number(a.sort_order) : 0;
        const sb = Number.isFinite(Number(b?.sort_order)) ? Number(b.sort_order) : 0;
        if (sa !== sb) return sa - sb;
        return (a?.id || 0) - (b?.id || 0);
    });
}

export function normalizeImageCrop(crop) {
    const x = Number(crop?.x ?? crop?.crop_x);
    const y = Number(crop?.y ?? crop?.crop_y);
    const zoom = Number(crop?.zoom ?? crop?.crop_zoom);
    const aspect = Number(crop?.aspect ?? crop?.crop_aspect);

    return {
        x: Number.isFinite(x) ? Math.min(100, Math.max(0, x)) : 50,
        y: Number.isFinite(y) ? Math.min(100, Math.max(0, y)) : 50,
        zoom: Number.isFinite(zoom) ? Math.min(3, Math.max(1, zoom)) : 1,
        aspect: Number.isFinite(aspect) ? aspect : null,
    };
}

export function cropImageStyle(crop) {
    const c = normalizeImageCrop(crop);
    return {
        objectFit: "cover",
        objectPosition: `${c.x}% ${c.y}%`,
        transform: `scale(${c.zoom})`,
        transformOrigin: `${c.x}% ${c.y}%`,
    };
}

export function revokeUrlSafe(url) {
    // Comments in English: Safely revoke object URLs without throwing.
    try {
        if (url) URL.revokeObjectURL(url);
    } catch {
        // ignore
    }
}
